const getLogicalOperator = (operator) => {
	switch (operator) {
		case "AND":
			return "$and";
		case "OR":
			return "$or";
		case "NOT":
			return "$nor";
		default:
			break;
	}
	return null;
};

const getOperator = (operator) => {
	switch (operator) {
		case ":":
			return "$eq";
		case ">":
			return "$gt";
		case ">=":
			return "$gte";
		case "<":
			return "$lt";
		case "<=":
			return "$lte";
		default:
			break;
	}
	return null;
};

const isSanitizedRegex = (value = "") => value.startsWith("/") || (value.endsWith("/") && !value.endsWith("\\/"));

const getSelfIdentifier = (value, localValue = "self") => {
	if (localValue === "self") {
		return value;
	}
	return localValue || "";
};

const getTypeIdentifier = (value, localValue = "") => {
	if (localValue === "self") {
		return value;
	}
	return "StringLiteral";
};

const resolveExpression = (args) => {
	const { operator, value, type = "", regex = false } = args;

	if (!operator || !value) {
		return null;
	}
	if (regex || type === "RegexExpression") {
		const regexValue = isSanitizedRegex(value) ? value.replace(/(^\/|\/$)/g, "") : `^${value}$`;
		return { $regex: `${regexValue}`.trim(), $options: "im" };
	}
	return { [operator]: type === "NumericLiteral" ? value : `${value}`.trim() };
};

const getMappedDefinitions = (match = null, args = null) => {
	if (!match) {
		return null;
	}

	const { operator, value, type } = args;

	const { value: valueDef, operator: operatorDef, type: typeDef } = match;

	const matchValue = getSelfIdentifier(value, valueDef);
	const matchOperator = getSelfIdentifier(operator, operatorDef);
	const matchType = getTypeIdentifier(type, typeDef);

	return { ...match, operator: matchOperator, value: matchValue, type: matchType };
};

const getMappedMatches = (matches = [], args = null) => {
	const { valueDefinition = null, operator = "$eq" } = args;

	if (!matches?.length) {
		return null;
	}

	// Mapped definitions
	matches = matches.map((match) => getMappedDefinitions(match, { operator, ...valueDefinition }));

	return matches.reduce((acc, match) => {
		const { property = "" } = match;
		if (!property) {
			return acc;
		}
		const expression = resolveExpression(match);
		return { ...acc, [property]: { ...expression } };
	}, {});
};

const getMappedResolvers = (resolver, args = null) => {
	if (!resolver) {
		return [];
	}

	const { paths = [""], matches = [] } = resolver;
	return [{ $or: paths.map((path) => ({ [path]: { ["$elemMatch"]: getMappedMatches(matches, args) } })) }];
};

const generatePipeline = (ast, resolvers) => {
	switch (ast.type) {
		case "LogicalExpression": {
			let logicalOperator = getLogicalOperator(ast.operator);
			if (ast.operator === "NOT") {
				return { [logicalOperator]: [generatePipeline(ast.right, resolvers)] };
			}
			return { [logicalOperator]: [generatePipeline(ast.left, resolvers), generatePipeline(ast.right, resolvers)] };
		}
		case "BinaryExpression":
			switch (ast.operator) {
				case ":":
				case ">":
				case ">=":
				case "<":
				case "<=": {
					const operator = getOperator(ast.operator);
					const left = generatePipeline(ast.left, resolvers);
					if (left.type === "ResolverExpression") {
						return generatePipeline({ ...left, valueDefinition: ast.right, operator }, resolvers);
					}
					return { [operator]: [left, ast.right.value] };
				}
				case "AND":
				case "OR":
				case "NOT": {
					let logicalOperator = getLogicalOperator(ast.operator);
					return { [logicalOperator]: [generatePipeline(ast.left, resolvers), generatePipeline(ast.right, resolvers)] };
				}
				default:
					throw new Error("Invalid operator: " + ast.operator);
			}
		case "RegexExpression":
			return ast.value.replace(/\//g, "").trim();
		case "Identifier":
			return {
				type: "ResolverExpression",
				values: resolvers?.[ast.value],
			};
		case "ResolverExpression": {
			const mappedResolvers = getMappedResolvers(ast.values, { ...ast });
			return mappedResolvers?.reduce((acc, resolver) => ({ ...acc, ...resolver }), {});
		}
		default:
			throw new Error("Invalid AST node type: " + ast.type);
	}
};

export { generatePipeline };
